// Entry module: collects DOM refs, builds shared context/state + a tiny pub/sub,
// wires the managers together and bootstraps the Konva stage.

import { createCanvasManager } from 'pc-canvas-manager';
import { createLayerManager } from 'pc-layer-manager';
import { createObjectFactory } from 'pc-object-factory';
import { createUIManager } from 'pc-ui-manager';
import { createPropertiesPanel } from 'pc-properties-panel';
import { createToolbar } from 'pc-toolbar';
import { createLeftPanel } from 'pc-left-panel';
import { createImageGenerator } from 'pc-image-generator';
import { createHistoryManager } from 'pc-history-manager';

function initCustomizer(customizer) {
  var preview = customizer.querySelector('[data-customizer-preview]');
  var mount = customizer.querySelector('[data-customizer-stage]');
  var viewport = customizer.querySelector('[data-customizer-viewport]');
  var sizer = customizer.querySelector('[data-customizer-sizer]');
  var productJson = customizer.querySelector('[data-customizer-product-json]');
  var variantInput = customizer.querySelector('[data-customizer-variant]');
  var submitButton = customizer.querySelector('[data-customizer-submit]');
  var errorNode = customizer.querySelector('[data-customizer-error]');
  var imageUploadInput = customizer.querySelector('[data-customizer-image-upload]');
  var sceneInput = customizer.querySelector('[data-customizer-scene]');
  var previewInput = customizer.querySelector('[data-customizer-preview-input]');
  var previewFileInput = customizer.querySelector('[data-customizer-preview-file]');
  var uploadFields = customizer.querySelector('[data-customizer-upload-fields]');
  var textMetadataFields = customizer.querySelector('[data-customizer-text-metadata-fields]');
  var metadataInput = customizer.querySelector('[data-customizer-metadata]');
  var detailsInput = customizer.querySelector('[data-customizer-details]');
  var validationNode = customizer.querySelector('[data-customizer-validation]');
  var form = customizer.querySelector('form[data-type="add-to-cart-form"]');

  var productData = null;
  var baseImageUrl = preview && preview.dataset.imageSrc;
  var baseImageWidth = Number(preview && preview.dataset.imageWidth) || 1000;
  var baseImageHeight = Number(preview && preview.dataset.imageHeight) || 1000;
  var imageGenerateEndpoint = customizer.dataset.openaiImageEndpoint || '';
  var imageEditEndpoint = customizer.dataset.openaiImageEditEndpoint || '';

  var state = {
    stage: null,
    layer: null,
    imageNode: null,       // background image
    customNodes: [],       // all user‑added nodes (text + uploaded images)
    generatedImages: [],
    editedImages: [],
    transformer: null,
    selectedNodes: [],
    imageWidth: baseImageWidth,
    imageHeight: baseImageHeight,
    zoom: 1
  };

  if (!preview || !mount || !window.Konva) return;

  try {
    productData = productJson ? JSON.parse(productJson.textContent) : null;
  } catch (error) {
    productData = null;
  }

  function showError(error) {
    customizer.classList.add('is-konva-failed');
    if (errorNode) errorNode.classList.remove('m:hidden');
    console.error('Product customizer canvas failed', error);
  }

  // Minimal pub/sub so panels/toolbar react to selection and layer changes
  // without depending on each other directly.
  var listeners = {};
  function on(event, fn) {
    (listeners[event] || (listeners[event] = [])).push(fn);
  }
  function emit(event, payload) {
    (listeners[event] || []).forEach(function (fn) {
      fn(payload);
    });
  }

  var ctx = {
    customizer: customizer,
    preview: preview,
    mount: mount,
    viewport: viewport,
    sizer: sizer,
    variantInput: variantInput,
    submitButton: submitButton,
    errorNode: errorNode,
    imageUploadInput: imageUploadInput,
    sceneInput: sceneInput,
    previewInput: previewInput,
    previewFileInput: previewFileInput,
    uploadFields: uploadFields,
    textMetadataFields: textMetadataFields,
    metadataInput: metadataInput,
    detailsInput: detailsInput,
    validationNode: validationNode,
    form: form,
    baseImageUrl: baseImageUrl,
    baseImageWidth: baseImageWidth,
    baseImageHeight: baseImageHeight,
    imageGenerateEndpoint: imageGenerateEndpoint,
    imageEditEndpoint: imageEditEndpoint,
    productData: productData,
    state: state,
    on: on,
    emit: emit
  };

  var canvas = createCanvasManager(ctx);
  var layers = createLayerManager(ctx, { canvas: canvas });
  var factory = createObjectFactory(ctx, { canvas: canvas, layers: layers });
  var ui = createUIManager(ctx, { canvas: canvas, factory: factory, showError: showError });
  createPropertiesPanel(ctx);
  createLeftPanel(ctx, { factory: factory, layers: layers });
  createImageGenerator(ctx, { factory: factory });
  var history = createHistoryManager(ctx, { factory: factory, layers: layers });
  createToolbar(ctx, { canvas: canvas, layers: layers, history: history });

  ui.bindControls();

  function initStage() {
    var size = canvas.getStageSize();

    state.stage = new Konva.Stage({
      container: mount,
      width: size.width,
      height: size.height
    });

    state.layer = new Konva.Layer();
    state.stage.add(state.layer);

    state.imageNode = new Konva.Image({
      x: 0,
      y: 0,
      width: size.width,
      height: size.height,
      listening: false
    });
    state.layer.add(state.imageNode);

    state.transformer = layers.createTransformer();
    state.layer.add(state.transformer);

    canvas.applyZoom();

    state.stage.on('click tap', function (event) {
      if (event.target === state.stage || event.target === state.imageNode) {
        layers.selectNodes([]);
        return;
      }

      if (layers.isSelectableNode(event.target)) {
        layers.selectNodes([event.target]);
      }
    });

    canvas.setBackgroundImage(baseImageUrl, baseImageWidth, baseImageHeight).catch(showError);
    preview.classList.add('is-konva-ready');

    // Seed the canvas with one text node (also selects it and refreshes panels).
    factory.addText();
    ui.updateVariant();
  }

  // Initialise the canvas
  try {
    initStage();
  } catch (error) {
    showError(error);
  }
}

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('[data-product-customizer]').forEach(initCustomizer);
});
